import { WidgetChartData, WidgetTarget } from '@/dashboard/shared/types/widgets.types';
import { z } from 'zod';

/**
 * Zod schema for validating cluster widget chart data
 *
 * This schema validates the structure of cluster widget data including:
 * - Stats: Must be arrays with at least 1 item for each node type
 * - Targets: Validated separately via targets array
 */

/**
 * Schema for stat arrays - must have at least 1 item
 */
const statArraySchema = z.array(z.string()).min(1, 'Stat array cannot be empty');

/**
 * Schema for Cubrid cluster value
 */
const cubridValueSchema = z.object({
  groupId: z.string().default(''),
  category: z.literal('cubrid'),
  groupName: z.string(),
  stats: z.object({
    cubrid: statArraySchema,
    cubrid_broker: statArraySchema,
  }),
});

/**
 * Schema for Kafka cluster value
 */
const kafkaValueSchema = z.object({
  groupId: z.string(),
  category: z.literal('kafka_cluster'),
  groupName: z.string(),
  stats: z.object({
    kafka_cluster: statArraySchema,
    kafka_broker: statArraySchema,
  }),
});

/**
 * Schema for PostgreSQL Replication cluster value
 */
const postgresqlReplicationValueSchema = z.object({
  groupId: z.string(),
  category: z.literal('postgresql_replication_cluster'),
  groupName: z.string(),
  stats: z.object({}),
});

/**
 * Union schema for cluster value (Cubrid, Kafka, or PostgreSQL Replication)
 */
const clusterValueSchema = z.union([
  cubridValueSchema,
  kafkaValueSchema,
  postgresqlReplicationValueSchema,
]);

/**
 * Schema for a single cluster widget chart data item
 */
export const clusterChartDataItemSchema = z.object({
  value: clusterValueSchema,
});

/**
 * Schema for the entire chartData array
 */
export const clusterChartDataSchema = z.array(clusterChartDataItemSchema);

/**
 * Type inference from schema
 */
export type ClusterChartDataSchema = z.infer<typeof clusterChartDataSchema>;
export type ClusterChartDataItemSchema = z.infer<typeof clusterChartDataItemSchema>;
export type ClusterValueSchema = z.infer<typeof clusterValueSchema>;
export type ClusterNodeType =
  | 'cubrid'
  | 'cubrid_broker'
  | 'kafka_cluster'
  | 'kafka_broker'
  | 'postgresql_replication_cluster'
  | 'postgresql_replication_primary'
  | 'postgresql_replication_standby';

export interface ClusterWidgetChartData extends WidgetChartData {
  targets: WidgetTarget[];
  value: ClusterValueSchema;
}

export const TARGET_TYPES_BY_CATEGORY: Record<string, ClusterNodeType[]> = {
  cubrid: ['cubrid', 'cubrid_broker'],
  kafka_cluster: ['kafka_cluster'],
  postgresql_replication_cluster: ['postgresql_replication_cluster'],
};

export function getTargetTypesByCategory(category: string): ClusterNodeType[] {
  return TARGET_TYPES_BY_CATEGORY[category] ?? [];
}

export interface ClusterTagItem {
  groupTagId: string;
  tagId: string;
  tagType: string;
}

export type ClusterValueTags = Record<string, ClusterTagItem[]>;

export function ensureTargetType(chartData: ClusterWidgetChartData): void {
  const category = chartData.value?.category;
  if (!category) return;
  const targetTypes = getTargetTypesByCategory(category);
  if (targetTypes.length === 1) {
    const [targetType] = targetTypes;
    chartData.targets?.forEach((target) => {
      if (!target.targetType) {
        target.targetType = targetType;
      }
    });
  }
}

/**
 * Sync targets array back to value.tags before saving.
 * Preserves targetType information that would be lost in the backend.
 */
export function syncTargetsToValueTags(chartData: ClusterWidgetChartData): void {
  if (!chartData.targets?.length) return;

  const tags: ClusterValueTags = {};
  chartData.targets.forEach(({ tagKey, tagValue, tagType, targetType }) => {
    if (!targetType) return;
    if (!tags[targetType]) tags[targetType] = [];
    tags[targetType].push({
      groupTagId: tagKey,
      tagId: tagValue,
      tagType,
    });
  });

  if (Object.keys(tags).length === 0) return;
  (chartData.value as ClusterValueSchema & { tags?: ClusterValueTags }).tags = tags;
}

/**
 * Migrate value.tags to targets array.
 * value.tags is the authoritative source for targetType information,
 * so always overwrite targets when value.tags exists.
 */
export function migrateValueTagsToTargets(chartData: ClusterWidgetChartData): void {
  const value = chartData.value as ClusterValueSchema & { tags?: ClusterValueTags };
  if (!value?.tags) return;

  chartData.targets = Object.entries(value.tags).flatMap(([targetType, tags]) =>
    tags.map(({ groupTagId, tagId, tagType }) => ({
      tagKey: groupTagId,
      tagValue: tagId,
      tagType: tagType as WidgetTarget['tagType'],
      targetType,
    })),
  );

  delete value.tags;
}
